import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { Send, MessageSquare, ChevronDown, Users, User } from 'lucide-react';

function timeLabel(ts) {
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function Avatar({ name, size = 28 }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: size * 0.38, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export default function Chat() {
  const { staff } = useAuth();
  const [messages, setMessages]     = useState([]);
  const [staffList, setStaffList]   = useState([]);
  const [text, setText]             = useState('');
  const [activeRoom, setActiveRoom] = useState('general');
  const [dmTarget, setDmTarget]     = useState(null);
  const [unreadDMs, setUnreadDMs]   = useState({});
  const [minimized, setMinimized]   = useState(false);
  const [sending, setSending]       = useState(false);
  const bottomRef                   = useRef(null);
  const inputRef                    = useRef(null);
  const activeRoomRef               = useRef('general');

  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

  useEffect(() => {
    if (!staff?.id) return;
    loadStaff();
    loadMessages();
    const sub = supabase
      .channel(`chat_${staff.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, payload => {
        const msg = payload.new;
        const isForMe = msg.room === 'general' || msg.receiver_id === staff.id || msg.sender_id === staff.id;
        if (!isForMe) return;
        setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
        if (msg.receiver_id === staff.id && msg.sender_id !== staff.id && activeRoomRef.current !== msg.sender_id) {
          setUnreadDMs(prev => ({ ...prev, [msg.sender_id]: (prev[msg.sender_id] || 0) + 1 }));
        }
      })
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [staff?.id]);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [messages, activeRoom]);

  async function loadStaff() {
    const { data } = await supabase.from('staff').select('id, full_name, staff_category').eq('is_active', true).neq('id', staff?.id).order('full_name');
    setStaffList(data || []);
  }

  async function loadMessages() {
    const { data } = await supabase.from('chat_messages').select('*, sender:sender_id(id, full_name)').order('created_at', { ascending: true }).limit(300);
    setMessages(data || []);
  }

  function openDM(member) {
    setDmTarget(member);
    setActiveRoom(member.id);
    setUnreadDMs(prev => ({ ...prev, [member.id]: 0 }));
    setMinimized(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  function openGeneral() {
    setDmTarget(null);
    setActiveRoom('general');
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  function getRoomMessages() {
    if (activeRoom === 'general') return messages.filter(m => m.room === 'general');
    return messages.filter(m =>
      (m.sender_id === staff?.id && m.receiver_id === dmTarget?.id) ||
      (m.receiver_id === staff?.id && m.sender_id === dmTarget?.id)
    );
  }

  async function sendMessage() {
    if (!text.trim() || !staff?.id || sending) return;
    setSending(true);
    const payload = {
      sender_id: staff.id,
      receiver_id: activeRoom === 'general' ? null : dmTarget?.id,
      message: text.trim(),
      room: activeRoom === 'general' ? 'general' : activeRoom,
      is_read: false,
    };
    const { data, error } = await supabase.from('chat_messages').insert(payload).select('*, sender:sender_id(id, full_name)').single();
    if (!error && data) setMessages(prev => prev.find(m => m.id === data.id) ? prev : [...prev, data]);
    setText('');
    setSending(false);
    inputRef.current?.focus();
  }

  const roomMessages = getRoomMessages();
  const totalUnread = Object.values(unreadDMs).reduce((a, b) => a + b, 0);
  const isGeneral = activeRoom === 'general';

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9998, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {!minimized && (
        <div style={{ width: 360, height: 500, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden', marginBottom: 8 }}>

          {/* Header */}
          <div style={{ background: 'var(--accent)', color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={16} />
              <span style={{ fontWeight: 700, fontSize: 13 }}>{isGeneral ? '👥 Group Chat' : `💬 ${dmTarget?.full_name}`}</span>
            </div>
            <button onClick={() => setMinimized(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
              <ChevronDown size={14} />
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--surface)' }}>
            <button onClick={openGeneral} style={{ flex: 1, padding: '9px 0', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'transparent', color: isGeneral ? 'var(--accent)' : 'var(--text3)', borderBottom: isGeneral ? '2px solid var(--accent)' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 0.15s' }}>
              <Users size={13} /> Group Chat
            </button>
            <button onClick={() => { if (dmTarget) { setActiveRoom(dmTarget.id); } else if (staffList[0]) { openDM(staffList[0]); } }} style={{ flex: 1, padding: '9px 0', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'transparent', color: !isGeneral ? 'var(--accent)' : 'var(--text3)', borderBottom: !isGeneral ? '2px solid var(--accent)' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 0.15s', position: 'relative' }}>
              <User size={13} /> Direct
              {totalUnread > 0 && <span style={{ background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 8, minWidth: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{totalUnread}</span>}
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

            {/* Staff sidebar for DM */}
            {!isGeneral && (
              <div style={{ width: 115, borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg3)', flexShrink: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', padding: '8px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Staff</div>
                {staffList.length === 0 ? (
                  <div style={{ padding: 10, fontSize: 11, color: 'var(--text3)' }}>No staff</div>
                ) : staffList.map(s => (
                  <div key={s.id} onClick={() => openDM(s)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 10px', cursor: 'pointer', background: dmTarget?.id === s.id ? 'var(--accent-light)' : 'transparent', borderLeft: `2px solid ${dmTarget?.id === s.id ? 'var(--accent)' : 'transparent'}`, position: 'relative' }}>
                    <Avatar name={s.full_name} size={26} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: dmTarget?.id === s.id ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: dmTarget?.id === s.id ? 'var(--accent)' : 'var(--text)' }}>{s.full_name?.split(' ')[0]}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.staff_category?.replace('_', ' ')}</div>
                    </div>
                    {unreadDMs[s.id] > 0 && <span style={{ background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 8, minWidth: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', flexShrink: 0 }}>{unreadDMs[s.id]}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Messages */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {!isGeneral && !dmTarget ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, marginTop: 50, padding: '0 16px' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>👈</div>
                    <div>Select a staff member to send a direct message</div>
                  </div>
                ) : roomMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, marginTop: 50 }}>
                    {isGeneral ? '👋 Be the first to say hello!' : `💬 Start chatting with ${dmTarget?.full_name?.split(' ')[0]}`}
                  </div>
                ) : roomMessages.map((m, i) => {
                  const isMe = m.sender_id === staff?.id;
                  const showName = !isMe && isGeneral && (i === 0 || roomMessages[i-1]?.sender_id !== m.sender_id);
                  return (
                    <div key={m.id || i} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', marginBottom: 2 }}>
                      {showName && <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2, paddingLeft: 4 }}>{m.sender?.full_name}</div>}
                      <div style={{ maxWidth: '82%', background: isMe ? 'var(--accent)' : 'var(--bg3)', color: isMe ? '#fff' : 'var(--text)', padding: '8px 11px', borderRadius: isMe ? '12px 12px 3px 12px' : '12px 12px 12px 3px', fontSize: 12.5, lineHeight: 1.45, wordBreak: 'break-word', border: isMe ? 'none' : '1px solid var(--border)' }}>
                        {m.message}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, paddingLeft: 4, paddingRight: 4 }}>{timeLabel(m.created_at)}</div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              {(isGeneral || dmTarget) && (
                <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6, flexShrink: 0 }}>
                  <input
                    ref={inputRef}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder={isGeneral ? 'Message everyone...' : `Message ${dmTarget?.full_name?.split(' ')[0]}...`}
                    style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 20, padding: '7px 13px', fontSize: 12.5, outline: 'none', background: 'var(--bg3)' }}
                  />
                  <button onClick={sendMessage} disabled={!text.trim() || sending} style={{ background: text.trim() ? 'var(--accent)' : 'var(--border)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: text.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Send size={14} color="#fff" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button onClick={() => setMinimized(m => !m)} style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--accent)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,176,155,0.4)', position: 'relative' }}>
        {minimized ? <MessageSquare size={22} color="#fff" /> : <ChevronDown size={22} color="#fff" />}
        {minimized && totalUnread > 0 && <span style={{ position: 'absolute', top: 2, right: 2, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{totalUnread}</span>}
      </button>
    </div>
  );
}
